/**
 * CommandPalette — global transaction search (note/payee with LIKE).
 *
 * Results with category/wallet/amount/date. Two exits:
 *   - Enter or click on a result → navigate to /transactions with `txId` in
 *     search params; tx-list-view detects it and opens edit dialog
 *     (the param auto-cleans after opening).
 *   - "Filter list" → navigate to /transactions with `text` applied to
 *     filter bar persisted in the URL.
 *
 * Keyboard: respects PALETTE_NEXT/PREV/CONFIRM bindings declared in
 * lib/shortcuts (scope "palette", activated with useScope while open).
 */
import { createEffect, createMemo, createSignal, For, on, onCleanup, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Portal } from "solid-js/web";
import { useNavigate } from "@tanstack/solid-router";
import { ArrowRight, Search } from "@fleet/icons";
import { cn, formatMoney, type TransactionWithMeta } from "@cashsync/shared";
import { useTransactionsQuery } from "@cashsync/shared/solid";
import { useI18n } from "@/providers/i18n-provider";
import { useFinanceDeps } from "@/features/finance/hooks/use-finance-deps";
import { resolveCategoryIcon } from "@/features/categories/category-icons";
import { categoryDisplayName } from "@/features/categories/category-display";
import { COMMAND_IDS } from "@/lib/shortcuts/defaults";
import { useCommandHandler, useScope } from "@fleet/shortcuts";
import { useBackDismiss } from "@/lib/use-back-dismiss";

const QUERY_DEBOUNCE_MS = 200;
const MAX_RESULTS = 30;

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CommandPalette(props: CommandPaletteProps) {
  // Browser back closes the palette instead of navigating away (PWA pattern).
  useBackDismiss(
    () => props.isOpen,
    () => {
      props.onClose();
    },
  );

  return (
    <Show when={props.isOpen}>
      <Portal>
        <PaletteContent
          onClose={() => {
            props.onClose();
          }}
        />
      </Portal>
    </Show>
  );
}

/** Mounted only while palette is open (queries and scope included). */
function PaletteContent(props: { onClose: () => void }) {
  const { t, locale } = useI18n();
  const deps = useFinanceDeps();
  const navigate = useNavigate();

  const [query, setQuery] = createSignal("");
  const [debounced, setDebounced] = createSignal("");
  const [activeIndex, setActiveIndex] = createSignal(0);
  let inputRef: HTMLInputElement | undefined;
  let listRef: HTMLDivElement | undefined;

  queueMicrotask(() => inputRef?.focus());

  let debounceTimer: ReturnType<typeof setTimeout> | undefined;
  createEffect(
    on(query, (q) => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => setDebounced(q.trim()), QUERY_DEBOUNCE_MS);
    }),
  );
  onCleanup(() => {
    clearTimeout(debounceTimer);
  });

  // Without text: recent transactions (quick access); with text: LIKE note/payee.
  const results = useTransactionsQuery({
    ...deps,
    filters: () => ({ text: debounced() || undefined }),
    limit: () => MAX_RESULTS,
  });

  const rows = createMemo(() => results.data ?? []);

  createEffect(
    on(rows, () => {
      setActiveIndex(0);
    }),
  );

  const openResult = (row: TransactionWithMeta | undefined) => {
    if (!row) {
      return;
    }
    props.onClose();
    void navigate({ to: "/transactions", search: { txId: row.id } });
  };

  const applyAsFilter = () => {
    const text = debounced();
    props.onClose();
    void navigate({ to: "/transactions", search: text ? { text } : {} });
  };

  const scrollActiveIntoView = () => {
    const el = listRef?.querySelector<HTMLElement>(`[data-index="${activeIndex()}"]`);
    el?.scrollIntoView({ block: "nearest" });
  };

  // Bindings declarados en defaults (scope "palette"): ↑/↓/Enter.
  useScope("palette");
  useCommandHandler(COMMAND_IDS.PALETTE_NEXT, () => {
    setActiveIndex((i) => Math.min(i + 1, Math.max(rows().length - 1, 0)));
    scrollActiveIntoView();
  });
  useCommandHandler(COMMAND_IDS.PALETTE_PREV, () => {
    setActiveIndex((i) => Math.max(i - 1, 0));
    scrollActiveIntoView();
  });
  useCommandHandler(COMMAND_IDS.PALETTE_CONFIRM, () => {
    openResult(rows()[activeIndex()]);
  });
  useCommandHandler(COMMAND_IDS.PALETTE_CLOSE, () => {
    props.onClose();
  });

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      props.onClose();
    }
  };

  return (
    <div class="fixed inset-0 z-[200] flex items-start justify-center pt-[15vh]">
      <button
        type="button"
        class="absolute inset-0 h-full w-full cursor-default border-none bg-black/40 p-0 backdrop-blur-[2px]"
        aria-label={t(msg`Close search`)}
        onClick={() => {
          props.onClose();
        }}
      />
      <div
        class="relative mx-4 flex max-h-[60vh] w-full max-w-xl flex-col overflow-hidden rounded-xl border border-border/60 bg-card shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label={t(msg`Search transactions`)}
      >
        <div class="flex items-center gap-3 border-b border-border/40 px-4 py-3">
          <Search class="h-4 w-4 shrink-0 text-muted-foreground" />
          <input
            ref={inputRef}
            value={query()}
            onInput={(e) => setQuery(e.currentTarget.value)}
            onKeyDown={handleKeyDown}
            placeholder={t(msg`Search transactions...`)}
            aria-label={t(msg`Search transactions...`)}
            class="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
        </div>

        <div ref={listRef} class="flex-1 overflow-y-auto py-1">
          <Show
            when={rows().length > 0}
            fallback={
              <div class="px-4 py-8 text-center text-sm text-muted-foreground">
                {debounced()
                  ? t(msg`No transactions match your search.`)
                  : t(msg`No transactions yet.`)}
              </div>
            }
          >
            <For each={rows()}>
              {(row, index) => (
                <ResultRow
                  row={row}
                  locale={locale()}
                  index={index()}
                  active={activeIndex() === index()}
                  onHover={() => setActiveIndex(index())}
                  onSelect={() => {
                    openResult(row);
                  }}
                />
              )}
            </For>
          </Show>
        </div>

        <div class="flex items-center justify-between gap-2 border-t border-border/40 px-4 py-2">
          <span class="hidden text-[11px] text-muted-foreground sm:block">
            {t(msg`↑↓ navigate · Enter opens · Esc closes`)}
          </span>
          <button
            type="button"
            class="ml-auto flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-primary hover:bg-accent/10"
            onClick={applyAsFilter}
          >
            {(() => {
              const searchQuery = debounced();
              return searchQuery
                ? t(msg`Filter the list by “${searchQuery}”`)
                : t(msg`Open transactions list`);
            })()}
            <ArrowRight class="h-3 w-3" />
          </button>
        </div>
      </div>
    </div>
  );
}

function ResultRow(props: {
  row: TransactionWithMeta;
  locale: string;
  index: number;
  active: boolean;
  onHover: () => void;
  onSelect: () => void;
}) {
  const { t } = useI18n();
  const isTransfer = () => props.row.kind === "transfer";

  const title = () => {
    if (isTransfer()) {
      return `${props.row.wallet_name ?? "?"} → ${props.row.to_wallet_name ?? "?"}`;
    }
    return (
      categoryDisplayName(t, {
        name: props.row.category_name,
        name_key: props.row.category_name_key,
      }) || t(msg`Uncategorized`)
    );
  };

  const subtitle = () =>
    [props.row.payee, props.row.note, isTransfer() ? null : (props.row.wallet_name ?? null)]
      .filter(Boolean)
      .join(" · ");

  const dateLabel = () => {
    const iso = props.row.occurred_at;
    if (!iso) {
      return "";
    }
    return new Intl.DateTimeFormat(props.locale, { day: "numeric", month: "short" }).format(
      new Date(iso),
    );
  };

  return (
    <button
      type="button"
      data-index={props.index}
      class={cn(
        "flex w-full items-center gap-3 px-4 py-2 text-left",
        props.active ? "bg-accent/15" : "hover:bg-accent/5",
      )}
      onMouseEnter={() => {
        props.onHover();
      }}
      onClick={() => {
        props.onSelect();
      }}
    >
      <span
        class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
        style={{ "background-color": `${props.row.category_color ?? "#9CA3AF"}22` }}
      >
        {(() => {
          const Icon = resolveCategoryIcon(props.row.category_icon);
          return <Icon class="h-4 w-4" color={props.row.category_color ?? undefined} />;
        })()}
      </span>
      <div class="min-w-0 flex-1">
        <div class="truncate text-sm font-medium">{title()}</div>
        <Show when={subtitle()}>
          <div class="truncate text-xs text-muted-foreground">{subtitle()}</div>
        </Show>
      </div>
      <div class="shrink-0 text-right">
        <div
          class={cn(
            "text-sm font-semibold tabular-nums",
            props.row.kind === "expense" && "text-destructive",
            props.row.kind === "income" && "text-success",
          )}
        >
          {formatMoney(props.row.amount ?? 0, props.row.currency ?? "USD", props.locale)}
        </div>
        <div class="text-[10px] text-muted-foreground">{dateLabel()}</div>
      </div>
    </button>
  );
}
